import { useState, useEffect, useRef } from 'react';
// @ts-ignore - Supabase client typing issue
import { supabase } from '../lib/supabase';
import { Booking } from '../types/booking';
import { 
  RealtimePostgresChangesPayload, 
  RealtimeChannel,
  SupabaseClient
} from '@supabase/supabase-js';

// Type cast for supabase to avoid TypeScript errors
// This doesn't affect runtime behavior, just helps TypeScript understand the type
const typedSupabase = supabase as unknown as SupabaseClient;

interface UseBookingUpdatesOptions {
  filterByStatus?: string;
  companyId?: number;
  onUpdate?: (booking: Booking) => void;
  maxRetries?: number; // Maximum number of retry attempts
  initialBackoffDelay?: number; // Initial backoff delay in ms
  maxBackoffDelay?: number; // Maximum backoff delay in ms
  enableFallback?: boolean; // Whether to enable fallback polling
  enableRealtime?: boolean; // Whether to enable realtime at all
  fallbackInterval?: number; // Fallback polling interval in ms
  activeTab?: string; // Current active tab
  pollOnlyWhenTabIs?: string; // Only poll when this tab is active
}

// Define a more specific type for the payload
type BookingPayload = RealtimePostgresChangesPayload<{
  [key: string]: unknown;
  id?: number;
  company_id?: number;
  status?: string;
}>;

// Type guard to check if the payload has company_id
const hasCompanyId = (obj: unknown): obj is { company_id: number } => {
  return obj !== null && typeof obj === 'object' && 'company_id' in (obj as Record<string, unknown>);
};

/**
 * Fetches bookings using a regular API call as a fallback for when realtime fails
 * This is an optimized version that only fetches the minimum data needed for detecting changes
 */
const fetchBookings = async (
  filterByStatus?: string,
  companyId?: number
): Promise<Booking[]> => {
  try {
    
    // Build the query - only select essential fields instead of all data
    // This significantly reduces data transfer and processing
    let query = typedSupabase.from('bookings').select(
      'id, status, updated_at', 
      { count: 'exact' }
    );
    
    // Add filters if provided
    if (filterByStatus) {
      query = query.eq('status', filterByStatus);
    }
    
    if (companyId) {
      query = query.eq('company_id', companyId);
    }
    
    // Only fetch recent updates - bookings updated in the last 5 minutes
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    query = query.gte('updated_at', fiveMinutesAgo);
    
    // Limit the number of results to further optimize
    query = query.limit(20);
    
    // Execute the query
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching bookings changes:', error);
      return [];
    }
    
    return data as Booking[];
  } catch (error) {
    console.error('Exception fetching bookings changes:', error);
    return [];
  }
};

/**
 * Hook to subscribe to real-time updates for bookings
 * with retry logic and fallback mechanism
 */
export const useBookingUpdates = ({
  filterByStatus,
  companyId,
  onUpdate,
  maxRetries = 5,
  initialBackoffDelay = 1000,
  maxBackoffDelay = 30000,
  enableFallback = true,
  enableRealtime = true,
  fallbackInterval = 120000,
  activeTab = '',
  pollOnlyWhenTabIs = ''
}: UseBookingUpdatesOptions = {}) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFallbackActive, setIsFallbackActive] = useState(false);
  const retryCount = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fallbackIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const lastFetchTimeRef = useRef<number>(0);
  const shouldPollRef = useRef(pollOnlyWhenTabIs ? activeTab === pollOnlyWhenTabIs : true);
  // New AbortController reference to cancel in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Update shouldPollRef when activeTab changes
  useEffect(() => {
    // Get previous state to detect tab changes
    const wasPollingShouldPoll = shouldPollRef.current;
    // Update polling state
    shouldPollRef.current = pollOnlyWhenTabIs ? activeTab === pollOnlyWhenTabIs : true;
    
    // If we changed from polling to not polling (switched away from pending tab)
    if (wasPollingShouldPoll && !shouldPollRef.current) {
      // Cancel any in-flight requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
    
    // If we're in fallback mode and tab becomes active, trigger an immediate fetch
    if (isFallbackActive && shouldPollRef.current) {
      fetchAndUpdate();
    }
  }, [activeTab, pollOnlyWhenTabIs]);

  // Clear any timeouts
  const clearTimeouts = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current);
      fallbackIntervalRef.current = null;
    }
    
    // Also abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  // Fetch bookings and update
  const fetchAndUpdate = async () => {
    // Only fetch if we should be polling
    if (!shouldPollRef.current) {
      console.log('Skipping polling - tab not active');
      return;
    }
    
    // Avoid fetching too frequently (debounce)
    const now = Date.now();
    if (now - lastFetchTimeRef.current < 2000) return;
    
    lastFetchTimeRef.current = now;
    
    try {
      // Create a new AbortController for this request
      if (abortControllerRef.current) {
        // Cancel any existing request first
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      
      // Only check for pending approvals count - this is much more efficient than
      // checking all status counts when we just need to know if there are new pending bookings
      const { count, error } = await typedSupabase
        .from('bookings')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'submitted') // Only count pending approvals
        .abortSignal(abortControllerRef.current.signal);
      
      if (error) {
        // Handle abort errors gracefully
        if (error.message && error.message.includes('aborted')) {
          return;
        }
        console.error('Error checking pending approval count:', error);
        return;
      }
      
      
      // Signal that we need to refresh without trying to update any state directly
      if (onUpdate) {
        // Use a minimal payload that won't interfere with other stats
        onUpdate({ 
          id: -999,  // Special ID that won't match any real booking
          status: "polling_refresh",  // Special status to avoid affecting counts
          created_at: new Date().toISOString(),
          is_refresh_only: true,
        } as unknown as Booking);
      }
    } catch (err) {
      // Ignore aborted fetch errors
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('Fetch aborted as expected');
        return;
      }
      console.error('Error in polling for changes:', err);
    } finally {
      // Clear the abort controller reference if we're done with it
      // But only if we're still on the active tab (might have changed during fetch)
      if (shouldPollRef.current && abortControllerRef.current) {
        abortControllerRef.current = null;
      }
    }
  };

  // Set up fallback polling mechanism
  const setupFallback = () => {
    if (!enableFallback || fallbackIntervalRef.current) return;
    
    setIsFallbackActive(true);
    
    // Initial fetch (only if we should be polling)
    if (shouldPollRef.current) {
      fetchAndUpdate();
    }
    
    // Set up interval for subsequent fetches
    fallbackIntervalRef.current = setInterval(() => {
      // Check if we should poll inside the interval function
      if (shouldPollRef.current) {
        fetchAndUpdate();
      }
    }, fallbackInterval);
  };

  // Subscribe to realtime updates with retry logic
  const subscribeToUpdates = () => {
    // Clean up any existing channel
    if (channelRef.current) {
      typedSupabase.removeChannel(channelRef.current);
    }
    
    try {
      // Create a new channel
      const channel = typedSupabase
        .channel('bookings-updates')
        .on(
          'postgres_changes',
          {
            event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
            schema: 'public',
            table: 'bookings',
            // Optional filter if provided
            filter: filterByStatus 
              ? `status=eq.${filterByStatus}` 
              : undefined
          },
          (payload: BookingPayload) => {
            
            // Only handle updates for relevant company if filter provided
            if (companyId && payload.new && hasCompanyId(payload.new) && payload.new.company_id !== companyId) {
              return;
            }
            
            // Fire update callback if provided
            if (onUpdate && payload.new) {
              onUpdate(payload.new as unknown as Booking);
            }
          }
        )
        .subscribe((status: string) => {
          if (status === 'SUBSCRIBED') {
            setIsSubscribed(true);
            setError(null);
            retryCount.current = 0;
            
            // If fallback was active, disable it
            if (isFallbackActive) {
              clearTimeouts();
              setIsFallbackActive(false);
            }
          } else if (status === 'CHANNEL_ERROR') {
            console.error('Error subscribing to booking updates');
            setError('Failed to subscribe to booking updates');
            setIsSubscribed(false);
            
            // Retry subscription if we haven't exceeded max retries
            if (retryCount.current < maxRetries) {
              const delay = Math.min(
                initialBackoffDelay * Math.pow(2, retryCount.current),
                maxBackoffDelay
              );
              
              
              retryCount.current += 1;
              retryTimeoutRef.current = setTimeout(subscribeToUpdates, delay);
            } else {
              console.error(`Max retries (${maxRetries}) exceeded, falling back to polling`);
              // Switch to fallback mechanism
              setupFallback();
            }
          }
        });
      
      // Store the channel reference for cleanup
      channelRef.current = channel;
    } catch (e) {
      console.error('Exception setting up realtime subscription:', e);
      setError(`Failed to set up subscription: ${e instanceof Error ? e.message : String(e)}`);
      setIsSubscribed(false);
      
      // Switch to fallback mechanism on exception
      setupFallback();
    }
  };

  useEffect(() => {
    // Set up the initial subscription or go directly to fallback based on settings
    if (enableRealtime) {
      subscribeToUpdates();
    } else {
      // Skip realtime entirely and go straight to fallback
      setupFallback();
    }
    
    // Clean up on unmount
    return () => {
      if (channelRef.current) {
        typedSupabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      clearTimeouts();
      
      // Make sure to abort any in-flight requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      setIsSubscribed(false);
    };
  }, [filterByStatus, companyId, onUpdate]);

  return { 
    isSubscribed, 
    error, 
    isFallbackActive,
    retry: () => {
      retryCount.current = 0;
      clearTimeouts();
      setIsFallbackActive(false);
      subscribeToUpdates();
    },
    // Manual poll method that can be called from outside
    manualPoll: () => {
      if (isFallbackActive) {
        fetchAndUpdate();
      }
    },
    // New method to manually abort all in-flight requests
    cancelRequests: () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
  };
}; 